import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  FileButton,
  Group,
  Switch,
  Text,
  TextInput,
  Stack,
  Title,
  Badge,
  Tabs,
  Select,
  Table,
} from "@mantine/core";
import {
  IconArrowDown,
  IconArrowUp,
  IconPencil,
  IconTrash,
  IconUpload,
} from "@tabler/icons";
import { Page, FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import _ from "lodash";
import PageListHeader from "../../components/pageListHeader";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { invalidateRiskControlSettings } from "../../contexts/riskControlSettingsContext";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "../../components/pageList";
import LocalPageList from "../../components/localPageList";
import ActionCellRender from "../../components/actionCellRender";
import { useTranslation } from "react-i18next";
import {
  moveSourceOfFundOption,
  normalizeSourceOfFundOptions,
  serializeSourceOfFundOptions,
  slugifySourceOfFundKey,
  validateSourceOfFundOptions,
} from "../../helpers/riskControlSettingsUtils";
import {
  COUNTRY_RISK_CATEGORIES,
  COUNTRY_RISK_LEVEL_COLOR,
  COUNTRY_RISK_RATING_OPTIONS,
  categorizeCountryRisk,
  formatRiskScore,
  normalizeCountryRiskRatings,
  validateCountryRiskRatings,
} from "../../helpers/countryRiskRating";
import {
  countryChineseName,
  countrySelectOptions,
} from "../../helpers/countryNames";
import {
  normalizeFwdRelatedPersons,
  validateFwdRelatedPersons,
} from "../../helpers/fwdRelatedPersons";
import {
  normalizeFwdRelatedCorporates,
  validateFwdRelatedCorporates,
} from "../../helpers/fwdRelatedCorporates";

const CountryRiskCategoryPreview = ({ form }) => {
  const { t } = useTranslation();
  const category = categorizeCountryRisk(form?.values?.rating);
  if (!category) return null;

  return (
    <Stack spacing={4}>
      <Text size="sm">
        {t("Risk Score")}: {formatRiskScore(category.score, t)}
      </Text>
      <Text size="sm">
        {t("Risk Level")}: {t(category.level)}
      </Text>
    </Stack>
  );
};

const buildCountryFormSchema = (countryOptions) => ({
  title: "Country risk rating",
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "md" },
  initialValues: {
    country: "",
    rating: "",
  },
  validate: {
    country: (value) => (String(value || "").trim() ? null : "Country is required"),
    rating: (value) => (categorizeCountryRisk(value) ? null : "Rating is required"),
  },
  layout: {
    containers: [
      {
        key: "country-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          withBorder: false,
          withPaper: false,
          minHeight: 0,
        },
      },
    ],
    fields: [
      {
        name: "country",
        component: Select,
        parent: "country-section",
        props: {
          label: "Country",
          required: true,
          searchable: true,
          data: countryOptions,
          withinPortal: true,
        },
      },
      {
        name: "rating",
        component: Select,
        parent: "country-section",
        props: {
          label: "Rating",
          required: true,
          data: COUNTRY_RISK_RATING_OPTIONS,
          withinPortal: true,
        },
      },
      {
        name: "categoryPreview",
        component: CountryRiskCategoryPreview,
        parent: "country-section",
        props: {},
      },
    ],
  },
});

const buildRelatedPersonFormSchema = () => ({
  title: "Related person",
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "md" },
  initialValues: {
    englishName: "",
    chineseName: "",
    companyName: "",
    companyRole: "",
  },
  validate: {
    englishName: (value) =>
      String(value || "").trim() ? null : "English name is required",
    companyName: (value) =>
      String(value || "").trim() ? null : "Company name is required",
    companyRole: (value) =>
      String(value || "").trim() ? null : "Company role is required",
  },
  layout: {
    containers: [
      {
        key: "person-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          withBorder: false,
          withPaper: false,
          minHeight: 0,
        },
      },
    ],
    fields: [
      {
        name: "englishName",
        component: TextInput,
        parent: "person-section",
        props: {
          label: "English Name",
          required: true,
        },
      },
      {
        name: "chineseName",
        component: TextInput,
        parent: "person-section",
        props: {
          label: "Chinese Name",
        },
      },
      {
        name: "companyName",
        component: TextInput,
        parent: "person-section",
        props: {
          label: "Company Name",
          required: true,
        },
      },
      {
        name: "companyRole",
        component: TextInput,
        parent: "person-section",
        props: {
          label: "Company Role",
          required: true,
        },
      },
    ],
  },
});

const buildRelatedCorporateFormSchema = () => ({
  title: "Related corporate",
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "md" },
  initialValues: {
    englishName: "",
    chineseName: "",
    formerlyKnownEnglish: "",
    formerlyKnownChinese: "",
    countryOfIncorporation: "",
  },
  validate: {
    englishName: (value) =>
      String(value || "").trim() ? null : "English name is required",
  },
  layout: {
    containers: [
      {
        key: "corporate-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          withBorder: false,
          withPaper: false,
          minHeight: 0,
        },
      },
    ],
    fields: [
      {
        name: "englishName",
        component: TextInput,
        parent: "corporate-section",
        props: { label: "English Name", required: true },
      },
      {
        name: "chineseName",
        component: TextInput,
        parent: "corporate-section",
        props: { label: "Chinese Name" },
      },
      {
        name: "formerlyKnownEnglish",
        component: TextInput,
        parent: "corporate-section",
        props: { label: "Formerly known as - English" },
      },
      {
        name: "formerlyKnownChinese",
        component: TextInput,
        parent: "corporate-section",
        props: { label: "Formerly known as - Chinese" },
      },
      {
        name: "countryOfIncorporation",
        component: TextInput,
        parent: "corporate-section",
        props: { label: "Country of Incorporation" },
      },
    ],
  },
});

const buildOptionFormSchema = (isEdit = false) => ({
  title: isEdit ? "Edit source of fund" : "Add source of fund",
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "md" },
  initialValues: {
    label: "",
    requiresSpecify: false,
    enabled: true,
  },
  validate: {
    label: (value) => (String(value || "").trim() ? null : "Label is required"),
  },
  layout: {
    containers: [
      {
        key: "option-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          withBorder: false,
          withPaper: false,
          minHeight: 0,
        },
      },
    ],
    fields: [
      {
        name: "key",
        component: TextInput,
        parent: "option-section",
        props: {
          label: "Key",
          readOnly: true,
          disabled: true,
        },
      },
      {
        name: "label",
        component: TextInput,
        parent: "option-section",
        props: {
          label: "Label",
          required: true,
        },
      },
      {
        name: "requiresSpecify",
        component: Checkbox,
        parent: "option-section",
        props: {
          label: "Requires specify",
          type: "checkbox",
        },
      },
      {
        name: "enabled",
        component: Checkbox,
        parent: "option-section",
        props: {
          label: "Enabled",
          type: "checkbox",
        },
      },
    ],
  },
});

const buildOptionTableSchema = () => ({
  columns: [
    {
      field: "label",
      headerName: "Label",
      minWidth: 180,
    },
    {
      field: "key",
      headerName: "Key",
      minWidth: 140,
    },
    {
      field: "requiresSpecify",
      headerName: "Requires specify",
      minWidth: 120,
      cellRender: (_col, data) => (
        <Badge color={data.requiresSpecify ? "blue" : "gray"} variant="light">
          {data.requiresSpecify ? "Yes" : "No"}
        </Badge>
      ),
    },
    {
      field: "enabled",
      headerName: "Enabled",
      minWidth: 100,
      cellRender: (_col, data) => (
        <Badge color={data.enabled ? "green" : "gray"} variant="light">
          {data.enabled ? "Yes" : "No"}
        </Badge>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      isAction: true,
      minWidth: 140,
      cellRender: ActionCellRender,
      actions: [
        { name: "moveUp", label: "Move up", icon: IconArrowUp, props: {} },
        { name: "moveDown", label: "Move down", icon: IconArrowDown, props: {} },
        { name: "edit", label: "Edit", icon: IconPencil, props: {} },
        { name: "delete", label: "Delete", icon: IconTrash, props: {} },
      ],
    },
  ],
});

const RiskControlSetting = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [settingId, setSettingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [countryRows, setCountryRows] = useState([]);
  const [relatedRows, setRelatedRows] = useState([]);
  const [corporateRows, setCorporateRows] = useState([]);
  const [activeTab, setActiveTab] = useState("income");
  const [relatedTab, setRelatedTab] = useState("individual");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingCorporate, setImportingCorporate] = useState(false);
  const [ratingFilter, setRatingFilter] = useState("");

  const tableSchema = useMemo(() => buildOptionTableSchema(), []);
  const formSchema = useMemo(() => buildOptionFormSchema(), []);
  const countryOptions = useMemo(
    () => countrySelectOptions(countryRows.map((row) => row.country)),
    [countryRows]
  );
  const countryFormSchema = useMemo(
    () => buildCountryFormSchema(countryOptions),
    [countryOptions]
  );
  const relatedFormSchema = useMemo(() => buildRelatedPersonFormSchema(), []);
  const relatedTableSchema = useMemo(
    () => ({
      columns: [
        {
          field: "englishName",
          headerName: t("English Name"),
          minWidth: 220,
        },
        {
          field: "chineseName",
          headerName: t("Chinese Name"),
          minWidth: 140,
        },
        {
          field: "companyName",
          headerName: t("Company Name"),
          minWidth: 280,
        },
        {
          field: "companyRole",
          headerName: t("Company Role"),
          minWidth: 220,
        },
        {
          field: "action",
          headerName: t("Action"),
          isAction: true,
          minWidth: 100,
          cellRender: ActionCellRender,
          actions: [
            { name: "edit", label: "Edit", icon: IconPencil, props: {} },
            { name: "delete", label: "Delete", icon: IconTrash, props: {} },
          ],
        },
      ],
    }),
    [t]
  );
  const corporateFormSchema = useMemo(
    () => buildRelatedCorporateFormSchema(),
    []
  );
  const corporateTableSchema = useMemo(
    () => ({
      columns: [
        {
          field: "englishName",
          headerName: t("English Name"),
          minWidth: 240,
        },
        {
          field: "chineseName",
          headerName: t("Chinese Name"),
          minWidth: 160,
        },
        {
          field: "formerlyKnownEnglish",
          headerName: t("Formerly known as - English"),
          minWidth: 220,
        },
        {
          field: "formerlyKnownChinese",
          headerName: t("Formerly known as - Chinese"),
          minWidth: 180,
        },
        {
          field: "countryOfIncorporation",
          headerName: t("Country of Incorporation"),
          minWidth: 180,
        },
        {
          field: "action",
          headerName: t("Action"),
          isAction: true,
          minWidth: 100,
          cellRender: ActionCellRender,
          actions: [
            { name: "edit", label: "Edit", icon: IconPencil, props: {} },
            { name: "delete", label: "Delete", icon: IconTrash, props: {} },
          ],
        },
      ],
    }),
    [t]
  );
  const countryTableSchema = useMemo(
    () => ({
      columns: [
        {
          field: "country",
          headerName: t("Country"),
          minWidth: 280,
          cellRender: (_col, data) => {
            const chinese = countryChineseName(data.country);
            return (
              <Text size="sm">
                {data.country}
                {chinese ? (
                  <Text span color="dimmed">
                    {" "}
                    {chinese}
                  </Text>
                ) : null}
              </Text>
            );
          },
        },
        {
          field: "rating",
          headerName: t("Country Risk"),
          minWidth: 220,
          cellRender: (_col, data) => {
            const category = categorizeCountryRisk(data.rating);
            return t(category?.label || data.rating);
          },
        },
        {
          field: "score",
          headerName: t("Risk Score"),
          minWidth: 140,
          cellRender: (_col, data) => {
            const category = categorizeCountryRisk(data.rating);
            return category ? formatRiskScore(category.score, t) : "";
          },
        },
        {
          field: "level",
          headerName: t("Risk Level"),
          minWidth: 140,
          cellRender: (_col, data) => {
            const category = categorizeCountryRisk(data.rating);
            if (!category) return "";
            return (
              <Badge
                color={COUNTRY_RISK_LEVEL_COLOR[category.level] || "gray"}
                variant="light"
              >
                {t(category.level)}
              </Badge>
            );
          },
        },
        {
          field: "action",
          headerName: t("Action"),
          isAction: true,
          minWidth: 100,
          cellRender: ActionCellRender,
          actions: [
            { name: "edit", label: "Edit", icon: IconPencil, props: {} },
            { name: "delete", label: "Delete", icon: IconTrash, props: {} },
          ],
        },
      ],
    }),
    [t]
  );

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const result = await api.search({
        apiEntity: "setting",
        pageSize: 1,
      });

      if (!result || _.isEmpty(result.docs)) {
        setSettingId(null);
        setRows(normalizeSourceOfFundOptions());
        setCountryRows(normalizeCountryRiskRatings());
        setRelatedRows([]);
        setCorporateRows([]);
        return;
      }

      const setting = result.docs[0];
      setSettingId(setting._id);
      setRows(normalizeSourceOfFundOptions(setting.riskControl?.sourceOfFundOptions));
      setCountryRows(normalizeCountryRiskRatings(setting.countryRiskRatings));
      setRelatedRows(normalizeFwdRelatedPersons(setting.fwdRelatedPersons));
      setCorporateRows(normalizeFwdRelatedCorporates(setting.fwdRelatedCorporates));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  const moveRow = (rowIndex, direction) => {
    setRows((current) => moveSourceOfFundOption(current, rowIndex, direction));
  };

  const handleRowsChange = (nextRows) => {
    const existingKeys = new Set(
      rows.map((row) => row.key).filter(Boolean)
    );
    const normalized = normalizeSourceOfFundOptions(
      nextRows.map((row, index) => {
        if (row.key) return row;
        const key = slugifySourceOfFundKey(row.label, existingKeys);
        existingKeys.add(key);
        return { ...row, key, sortOrder: index };
      })
    );
    setRows(normalized);
  };

  const handleSave = async () => {
    const validationError = validateSourceOfFundOptions(rows);
    if (validationError) {
      setActiveTab("income");
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: validationError,
      });
      return;
    }

    const countryError = validateCountryRiskRatings(countryRows);
    if (countryError) {
      setActiveTab("country");
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: countryError,
      });
      return;
    }

    const relatedError = validateFwdRelatedPersons(relatedRows);
    if (relatedError) {
      setActiveTab("related");
      setRelatedTab("individual");
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: relatedError,
      });
      return;
    }

    const corporateError = validateFwdRelatedCorporates(corporateRows);
    if (corporateError) {
      setActiveTab("related");
      setRelatedTab("corporate");
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: corporateError,
      });
      return;
    }

    try {
      setSaving(true);
      let result;
      const riskControl = {
        sourceOfFundOptions: serializeSourceOfFundOptions(rows),
      };
      const countryRiskRatings = normalizeCountryRiskRatings(countryRows);
      const fwdRelatedPersons = normalizeFwdRelatedPersons(relatedRows);
      const fwdRelatedCorporates = normalizeFwdRelatedCorporates(corporateRows);

      if (settingId) {
        const existing = await api.getById({
          apiEntity: "setting",
          id: settingId,
        });
        result = await api.updateById({
          apiEntity: "setting",
          id: settingId,
          values: {
            ...existing,
            countryRiskRatings,
            fwdRelatedPersons,
            fwdRelatedCorporates,
            riskControl: {
              ...(existing.riskControl || {}),
              ...riskControl,
            },
          },
        });
      } else {
        result = await api.add({
          apiEntity: "setting",
          values: {
            riskControl,
            countryRiskRatings,
            fwdRelatedPersons,
            fwdRelatedCorporates,
          },
        });
        if (result?.success && result?.data?._id) {
          setSettingId(result.data._id);
        }
      }

      if (result?.success) {
        setCountryRows(countryRiskRatings);
        setRelatedRows(fwdRelatedPersons);
        setCorporateRows(fwdRelatedCorporates);
        invalidateRiskControlSettings();
        showNotification({
          title: t("Save"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message: t("Risk control settings saved."),
        });
        return;
      }

      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: api.getErrorMsg(result?.error) || t("Failed to save settings."),
      });
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("Failed to save settings."),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleImportRelatedPersons = async (file) => {
    if (!file) return;
    try {
      setImporting(true);
      const data = await api.importFwdRelatedPersons(file, settingId);
      const imported = normalizeFwdRelatedPersons(data?.fwdRelatedPersons);
      setRelatedRows(imported);
      if (data?.settingId) setSettingId(data.settingId);
      showNotification({
        title: t("Import"),
        color: "green",
        icon: <IconCircleCheck size={18} />,
        message: t("Related persons imported."),
      });
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("Import"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: error?.message || t("Failed to import related persons."),
      });
    } finally {
      setImporting(false);
    }
  };

  const handleImportRelatedCorporates = async (file) => {
    if (!file) return;
    try {
      setImportingCorporate(true);
      const data = await api.importFwdRelatedCorporates(file, settingId);
      const imported = normalizeFwdRelatedCorporates(data?.fwdRelatedCorporates);
      setCorporateRows(imported);
      if (data?.settingId) setSettingId(data.settingId);
      showNotification({
        title: t("Import"),
        color: "green",
        icon: <IconCircleCheck size={18} />,
        message: t("Related corporates imported."),
      });
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("Import"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: error?.message || t("Failed to import related corporates."),
      });
    } finally {
      setImportingCorporate(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title={t("Risk Control")}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled: loading,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />

      {!loading && (
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List mb="md">
            <Tabs.Tab value="income">{t("Client Income Source")}</Tabs.Tab>
            <Tabs.Tab value="country">{t("Country Risk List")}</Tabs.Tab>
            <Tabs.Tab value="related">{t("Company Related Party")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="income">
            <FormSectionCard>
              <Title order={5}>{t("Source of Fund")}</Title>
              <Text size="xs" color="dimmed" mb="md">
                {t(
                  "Configure the source of fund options shown on the client Risk Control form. Enable Requires specify to show a text field when the option is checked."
                )}
              </Text>
              <LocalPageList
                rows={rows}
                onFormSubmit={handleRowsChange}
                tableSchema={tableSchema}
                formSchema={formSchema}
                btnLabel="Add option"
                onActionBtnClick={({ action, rowIndex }) => {
                  if (action === "moveUp") moveRow(rowIndex, -1);
                  if (action === "moveDown") moveRow(rowIndex, 1);
                }}
              />
            </FormSectionCard>
          </Tabs.Panel>

          <Tabs.Panel value="country">
            <Stack spacing="md">
              <FormSectionCard>
                <Title order={5}>{t("Country risk categories")}</Title>
                <Text size="xs" color="dimmed" mb="md">
                  {t(
                    "Enter a rating for each country. The rating sets the risk score and risk level."
                  )}
                </Text>
                <Table striped fontSize="sm">
                  <thead>
                    <tr>
                      <th>{t("Country Risk")}</th>
                      <th>{t("Risk Score")}</th>
                      <th>{t("Risk Level")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COUNTRY_RISK_CATEGORIES.map((category) => (
                      <tr key={category.rating}>
                        <td>{t(category.label)}</td>
                        <td>{formatRiskScore(category.score, t)}</td>
                        <td>
                          <Badge
                            color={COUNTRY_RISK_LEVEL_COLOR[category.level]}
                            variant="light"
                          >
                            {t(category.level)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </FormSectionCard>

              <FormSectionCard>
                <Title order={5}>{t("Countries")}</Title>
                <Text size="xs" color="dimmed" mb="md">
                  {t(
                    "Screening passes when the risk level is Low or Medium. High and Unacceptable ask Operation for further handling."
                  )}
                </Text>
                <Group spacing="xs" mb="sm">
                  <Button
                    size="xs"
                    variant={ratingFilter === "" ? "filled" : "default"}
                    onClick={() => setRatingFilter("")}
                  >
                    {t("All")} ({countryRows.length})
                  </Button>
                  {COUNTRY_RISK_CATEGORIES.map((category) => {
                    const count = countryRows.filter(
                      (row) => row.rating === category.rating
                    ).length;
                    return (
                      <Button
                        key={category.rating}
                        size="xs"
                        variant={
                          ratingFilter === category.rating ? "filled" : "default"
                        }
                        onClick={() => setRatingFilter(category.rating)}
                      >
                        {t(category.label)} ({count})
                      </Button>
                    );
                  })}
                </Group>
                <LocalPageList
                  rows={countryRows}
                  onFormSubmit={setCountryRows}
                  tableSchema={countryTableSchema}
                  formSchema={countryFormSchema}
                  btnLabel="Add country"
                  rowFilter={(row) =>
                    !ratingFilter || row.rating === ratingFilter
                  }
                />
              </FormSectionCard>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="related">
            <Tabs value={relatedTab} onTabChange={setRelatedTab}>
              <Tabs.List mb="md">
                <Tabs.Tab value="individual">{t("Related Individual")}</Tabs.Tab>
                <Tabs.Tab value="corporate">{t("Related Corporate")}</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="individual">
                <FormSectionCard>
                  <Group position="apart" align="flex-start" mb="md">
                    <div>
                      <Title order={5}>{t("Related persons")}</Title>
                      <Text size="xs" color="dimmed" mt={4}>
                        {t(
                          "Import the related-party workbook. The Lead sheet is ignored. Common directors and company roles are kept."
                        )}
                      </Text>
                    </div>
                    <FileButton
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={handleImportRelatedPersons}
                    >
                      {(props) => (
                        <Button
                          {...props}
                          size="xs"
                          variant="light"
                          loading={importing}
                          leftIcon={<IconUpload size={14} />}
                        >
                          {t("Import")}
                        </Button>
                      )}
                    </FileButton>
                  </Group>
                  <LocalPageList
                    rows={relatedRows}
                    onFormSubmit={(nextRows) =>
                      setRelatedRows(normalizeFwdRelatedPersons(nextRows))
                    }
                    tableSchema={relatedTableSchema}
                    formSchema={relatedFormSchema}
                    btnLabel="Add related person"
                  />
                </FormSectionCard>
              </Tabs.Panel>

              <Tabs.Panel value="corporate">
                <FormSectionCard>
                  <Group position="apart" align="flex-start" mb="md">
                    <div>
                      <Title order={5}>{t("Related corporates")}</Title>
                      <Text size="xs" color="dimmed" mt={4}>
                        {t(
                          "Import the corporate workbook. Only company-list sheets are kept."
                        )}
                      </Text>
                    </div>
                    <FileButton
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={handleImportRelatedCorporates}
                    >
                      {(props) => (
                        <Button
                          {...props}
                          size="xs"
                          variant="light"
                          loading={importingCorporate}
                          leftIcon={<IconUpload size={14} />}
                        >
                          {t("Import")}
                        </Button>
                      )}
                    </FileButton>
                  </Group>
                  <LocalPageList
                    rows={corporateRows}
                    onFormSubmit={(nextRows) =>
                      setCorporateRows(normalizeFwdRelatedCorporates(nextRows))
                    }
                    tableSchema={corporateTableSchema}
                    formSchema={corporateFormSchema}
                    btnLabel="Add related corporate"
                  />
                </FormSectionCard>
              </Tabs.Panel>
            </Tabs>
          </Tabs.Panel>
        </Tabs>
      )}
    </Page>
  );
};

export default RiskControlSetting;
